import {
  IconBuildingBank,
  IconCheck,
  IconFileTypePdf,
  IconLayoutGrid,
  IconListCheck,
  IconScan,
  IconWorld,
  type IconProps,
} from "@tabler/icons-react";
import { useEffect, useState, type ComponentType } from "react";
import { Card, Group, Loader, Stack, Text } from "@/components/ui";

interface Paso {
  icon: ComponentType<IconProps>;
  label: string;
  desc: string;
}

const PASOS: Paso[] = [
  { icon: IconFileTypePdf, label: "Leyendo el PDF de la factura", desc: "Abriendo el documento y preparando el análisis" },
  { icon: IconLayoutGrid, label: "Detectando la plantilla del cliente", desc: "Bloomberg, NYSE, Alberto Calva, etc." },
  { icon: IconScan, label: "Extrayendo los datos por OCR", desc: "Emisor, receptor, importe, conceptos" },
  { icon: IconListCheck, label: "Validando la información extraída", desc: "Campos obligatorios, importe y fecha" },
  { icon: IconBuildingBank, label: "Derivando la organización", desc: "A partir del RFC receptor de la factura" },
];

// Animaciones scoped por prefijo `invv-` para no colisionar con otros estilos
// (mismo lenguaje visual que la validación del CFDI ante el SAT · `satv-`).
const CSS = `
@keyframes invv-spin { to { transform: rotate(360deg); } }
@keyframes invv-pulse {
  0%, 100% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--color-multiva-600) 45%, transparent); }
  50% { box-shadow: 0 0 0 12px color-mix(in srgb, var(--color-multiva-600) 0%, transparent); }
}
@keyframes invv-slide {
  0% { left: -45%; }
  100% { left: 100%; }
}
@keyframes invv-fadein {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes invv-scan {
  0% { top: 8%; opacity: 0; }
  15% { opacity: 1; }
  85% { opacity: 1; }
  100% { top: 82%; opacity: 0; }
}
.invv-orb { position: relative; width: 54px; height: 54px; flex-shrink: 0; }
.invv-orb-ring {
  position: absolute; inset: 0; border-radius: 50%;
  border: 3px solid color-mix(in srgb, var(--color-multiva-600) 22%, transparent);
  border-top-color: var(--color-multiva-600);
  animation: invv-spin 0.9s linear infinite;
}
.invv-orb-core {
  position: absolute; inset: 8px; border-radius: 50%; color: white; overflow: hidden;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, var(--color-multiva-600), var(--color-multiva-700));
  animation: invv-pulse 1.8s ease-out infinite;
}
/* Línea de "escaneo" que recorre el orbe · refuerza la idea de OCR sobre el PDF. */
.invv-orb-scan {
  position: absolute; left: 12%; right: 12%; height: 2px; border-radius: 2px;
  background: color-mix(in srgb, white 85%, transparent);
  box-shadow: 0 0 6px 1px color-mix(in srgb, white 60%, transparent);
  animation: invv-scan 1.6s ease-in-out infinite;
}
.invv-track {
  position: relative; height: 4px; border-radius: 2px; overflow: hidden;
  background: color-mix(in srgb, var(--color-multiva-600) 15%, transparent);
}
.invv-fill {
  position: absolute; top: 0; height: 100%; width: 45%; border-radius: 2px;
  background: linear-gradient(90deg, transparent, var(--color-multiva-600), transparent);
  animation: invv-slide 1.15s ease-in-out infinite;
}
.invv-step-icon {
  width: 30px; height: 30px; border-radius: 8px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  transition: background 250ms ease, color 250ms ease;
}
@media (prefers-reduced-motion: reduce) {
  .invv-orb-ring, .invv-orb-core, .invv-orb-scan, .invv-fill { animation: none; }
}
`;

/**
 * Panel animado que se muestra mientras el PDF del Invoice (Contrato Anualizado /
 * Orden de Compra) está en proceso de lectura y validación por OCR. Mismo lenguaje
 * visual que <SatValidacionProgreso/> (orbe giratorio con pulso + línea de escaneo,
 * barra de progreso indeterminada y una secuencia de pasos que avanza) para dar
 * feedback interactivo en vez de un simple botón "cargando". Los pasos avanzan solos
 * y se quedan en el último mientras siga la espera · no implica un porcentaje real.
 */
export function InvoiceValidacionProgreso() {
  const [activo, setActivo] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setActivo((a) => (a < PASOS.length - 1 ? a + 1 : a)), 1100);
    return () => clearInterval(t);
  }, []);

  return (
    <Card
      padding="lg"
      style={{
        borderColor: "color-mix(in srgb, var(--color-multiva-600) 35%, transparent)",
        background: "color-mix(in srgb, var(--color-multiva-600) 5%, var(--color-card))",
        animation: "invv-fadein 240ms ease",
      }}
    >
      <style>{CSS}</style>
      <Stack gap="md">
        <Group gap="md" wrap="nowrap" align="center">
          <div className="invv-orb">
            <div className="invv-orb-ring" />
            <div className="invv-orb-core">
              <IconWorld size={22} stroke={1.7} />
              <div className="invv-orb-scan" />
            </div>
          </div>
          <Stack gap={2} className="min-w-0 flex-1">
            <Text fw={700} size="sm">
              Validando la información de la factura…
            </Text>
            <Text size="xs" c="dimmed">
              Estamos leyendo el PDF y extrayendo los datos por OCR · esto puede tardar unos segundos,
              no cierres esta pantalla.
            </Text>
          </Stack>
        </Group>

        <div className="invv-track">
          <div className="invv-fill" />
        </div>

        <Stack gap="xs">
          {PASOS.map((p, idx) => {
            const completado = idx < activo;
            const enCurso = idx === activo;
            const Icon = p.icon;
            const bg = completado
              ? "color-mix(in srgb, var(--color-exito-600) 15%, transparent)"
              : enCurso
                ? "color-mix(in srgb, var(--color-multiva-600) 15%, transparent)"
                : "var(--color-muted)";
            const color = completado
              ? "var(--color-exito-600)"
              : enCurso
                ? "var(--color-multiva-600)"
                : "var(--color-muted-foreground)";
            return (
              <Group key={p.label} gap="sm" wrap="nowrap" style={{ opacity: completado || enCurso ? 1 : 0.5 }}>
                <div className="invv-step-icon" style={{ background: bg, color }}>
                  {completado ? (
                    <IconCheck size={16} stroke={2.2} />
                  ) : enCurso ? (
                    <Loader size="sm" color="multiva" />
                  ) : (
                    <Icon size={16} stroke={1.7} />
                  )}
                </div>
                <Stack gap={0} className="min-w-0">
                  <Text size="sm" fw={enCurso ? 600 : 500}>
                    {p.label}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {p.desc}
                  </Text>
                </Stack>
              </Group>
            );
          })}
        </Stack>
      </Stack>
    </Card>
  );
}
